import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchTicker } from '../api';

const TYPE_CONFIG = {
  review:   { emoji: '📝', color: '#0ea5e9',  bg: 'rgba(14,165,233,0.1)',   label: 'New Kahani' },
  upvote:   { emoji: '👍', color: '#10b981',  bg: 'rgba(16,185,129,0.1)',   label: 'Upvoted' },
  comment:  { emoji: '💬', color: '#8b5cf6',  bg: 'rgba(139,92,246,0.1)',   label: 'Comment' },
  reply:    { emoji: '↩️', color: '#0ea5e9',  bg: 'rgba(14,165,233,0.1)',   label: 'Reply' },
  reaction: { emoji: '❤️', color: '#f97316',  bg: 'rgba(249,115,22,0.1)',   label: 'Reaction' },
};

const STATUS_COLOR = {
  offer:    '#10b981',
  rejected: '#ef4444',
  ghosted:  '#f97316',
  ongoing:  '#8b5cf6',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function LiveFeed({ onActivityClick, isGuest, onSignInRequired }) {
  const [items, setItems] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [, setTick] = useState(0); // forces timestamp re-render
  const prevIdsRef = useRef(new Set());
  const isInitialLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchTicker();
      const incoming = data.items || [];

      if (isInitialLoad.current) {
        // First load: populate silently, no animation
        prevIdsRef.current = new Set(incoming.map(i => i.id));
        setItems(incoming);
        isInitialLoad.current = false;
        return;
      }

      // Subsequent polls: only prepend genuinely new items
      const freshItems = incoming.filter(it => !prevIdsRef.current.has(it.id));
      prevIdsRef.current = new Set(incoming.map(i => i.id));

      if (freshItems.length > 0) {
        const freshIds = new Set(freshItems.map(i => i.id));
        setNewIds(freshIds);
        setItems(prev => [...freshItems, ...prev].slice(0, 30));
        setTimeout(() => setNewIds(new Set()), 3000);
      }
    } catch (err) {
      console.error('LiveFeed fetch error:', err);
    }
  }, []);

  useEffect(() => {
    load();
    const pollInterval = setInterval(load, 10000); // poll every 10s
    const tickInterval = setInterval(() => setTick(t => t + 1), 30000); // refresh timestamps every 30s
    return () => { clearInterval(pollInterval); clearInterval(tickInterval); };
  }, [load]);

  const handleClick = (item) => {
    if (!item.reviewId || !onActivityClick) return;
    const openComments = item.type === 'comment' || item.type === 'reaction';
    onActivityClick(item.reviewId, openComments);
  };

  return (
    <aside data-tour="live" style={{
      position: 'sticky', top: 76, marginTop: 24,
      height: 'calc(100vh - 96px)',
      display: 'flex', flexDirection: 'column',
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 16, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px 10px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 8,
        flexShrink: 0,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#10b981',
          boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
          animation: 'livePulse 2s ease infinite',
          flexShrink: 0,
        }} />
        <span style={{
          fontFamily: 'var(--sans)', fontWeight: 800, fontSize: 13,
          color: 'var(--text)', letterSpacing: 0.2,
        }}>
          Live Activity
        </span>
      </div>

      {/* Feed */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '4px 0',
        scrollbarWidth: 'thin',
      }}>
        {isGuest ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', padding: '24px 20px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
            <div style={{ fontFamily: 'var(--sans)', fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>
              Sign in to view Live Activity
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18, lineHeight: 1.5 }}>
              See real-time upvotes, comments and new kahanis from the community.
            </div>
            <button
              onClick={onSignInRequired}
              style={{
                background: 'var(--blue)', color: '#fff', border: 'none',
                padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--sans)',
                boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
              }}
            >
              Sign In / Sign Up
            </button>
          </div>
        ) : items.length === 0 ? (
          <div style={{
            padding: '40px 16px', textAlign: 'center',
            color: 'var(--text3)', fontSize: 12,
          }}>
            No activity yet...
          </div>
        ) : (
          items.map((item) => {
            const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.review;
            const isNew = newIds.has(item.id);
            const clickable = !!item.reviewId && !!onActivityClick;

            return (
              <div
                key={item.id}
                onClick={() => handleClick(item)}
                style={{
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--border)',
                  animation: isNew ? 'liveSlideIn 0.4s ease-out' : 'none',
                  background: isNew ? cfg.bg : 'transparent',
                  transition: 'background 1.5s ease',
                  cursor: clickable ? 'pointer' : 'default',
                }}
                onMouseEnter={e => { if (clickable) e.currentTarget.style.background = 'var(--surface2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isNew ? cfg.bg : 'transparent'; }}
              >
                {/* Type badge + time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: cfg.color,
                    background: cfg.bg, padding: '2px 7px', borderRadius: 20,
                    fontFamily: 'var(--sans)',
                  }}>
                    {cfg.emoji} {cfg.label}
                  </span>
                  {isNew && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: '#fff',
                      background: '#10b981', padding: '1px 6px', borderRadius: 20,
                      fontFamily: 'var(--sans)', animation: 'liveFadeOut 3s ease forwards',
                    }}>
                      NEW
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {timeAgo(item.createdAt)}
                  </span>
                </div>

                {/* Activity content */}
                {item.type === 'review' ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4, marginBottom: 2 }}>
                      {item.role} at {item.company}
                    </div>
                    {item.status && (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: STATUS_COLOR[item.status] || 'var(--text3)',
                        background: `${STATUS_COLOR[item.status]}18` || 'var(--surface2)',
                        padding: '1px 6px', borderRadius: 10,
                      }}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 700, color: 'var(--blue)' }}>{item.actorId || item.actorHkId || item.authorHkId || '?'}</span>
                    {item.type === 'upvote'   && <> upvoted kahani at <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.company}</span></>}
                    {item.type === 'comment'  && <> commented on kahani at <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.company}</span></>}
                    {item.type === 'reply'    && <> replied on kahani at <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.company}</span></>}
                    {item.type === 'reaction' && <> reacted {item.emoji} to comment on kahani at <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.company}</span></>}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(16,185,129,0.25); }
          50% { box-shadow: 0 0 0 6px rgba(16,185,129,0.05); }
        }
        @keyframes liveSlideIn {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes liveFadeOut {
          0%   { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </aside>
  );
}
